import { useId, useState } from 'react';

import { KeyboardIcon } from 'lucide-react';
import { Controller, useFormContext } from 'react-hook-form';

import { cn } from '@nesso/ui/lib/utils';
import { FieldError } from '@nesso/ui/primitives/field';
import { InputGroup, InputGroupAddon, InputGroupInput } from '@nesso/ui/primitives/input-group';
import { RadioGroup } from '@nesso/ui/primitives/radio-group';

import { OTHER_PRONOUN_CHOICE, PRONOUN_OPTIONS } from '@/shared/constants/pronouns';
import { derivePronounSelection, hasCustomPronoun } from '@/shared/lib/derive-pronoun-selection';

import { RadioButtonItem } from './radio-button-item';

type PronounsFormValues = { pronouns: string | null };

type Props = {
    label: string;
    maxLength: number;
    optionsClassName?: string;
};

// Other is sticky state rather than derived from the text: deriving it would deselect Other, and
// unmount the input showing the error, on the keystroke that clears the text. It is seeded from
// `formState.defaultValues`, so a saved custom pronoun opens as Other.
export function PronounsFields({ label, maxLength, optionsClassName = 'w-50' }: Props) {
    const {
        control,
        trigger,
        formState: { isSubmitting, defaultValues },
    } = useFormContext<PronounsFormValues>();
    const [otherManuallySelected, setOtherManuallySelected] = useState(() =>
        hasCustomPronoun(defaultValues?.pronouns),
    );
    // The input mounts both when the therapist picks Other and when a saved custom pronoun opens, and
    // only the first should take focus. Not `otherManuallySelected`: it is already true on mount for
    // a saved custom value.
    const [otherChosenThisSession, setOtherChosenThisSession] = useState(false);
    const labelId = useId();
    const errorId = useId();

    return (
        <Controller
            control={control}
            name="pronouns"
            render={({ field, fieldState }) => {
                const { isOtherSelected, radioValue } = derivePronounSelection(
                    field.value,
                    otherManuallySelected,
                );

                return (
                    <div className="flex flex-col gap-3">
                        <p id={labelId} className="text-base">
                            {label}
                        </p>
                        <RadioGroup
                            aria-labelledby={labelId}
                            value={radioValue}
                            onValueChange={(next) => {
                                // Other deliberately does not validate: it writes '' by construction, so
                                // validating here would flag a box the therapist has not typed into yet.
                                // The error belongs to leaving the revealed input, not to revealing it.
                                if (next === OTHER_PRONOUN_CHOICE) {
                                    setOtherManuallySelected(true);
                                    setOtherChosenThisSession(true);
                                    field.onChange('');
                                    return;
                                }
                                setOtherManuallySelected(false);
                                field.onChange(next);
                                // A pronoun choice is complete the moment it is made, so it validates on
                                // selection, sooner than blur — which also clears an error the input raised.
                                void trigger('pronouns');
                            }}
                            disabled={isSubmitting}
                            className={cn('grid gap-6 md:gap-3', optionsClassName)}
                        >
                            {PRONOUN_OPTIONS.map((option) => (
                                <RadioButtonItem
                                    key={option.value}
                                    value={option.value}
                                    title={option.label}
                                    size="sm"
                                />
                            ))}
                            {isOtherSelected ? (
                                // A bare input rather than `InputField`: its label is always visible, and
                                // this slot sits under the heading, so it would read as a second field.
                                <div className="flex flex-col gap-3">
                                    <InputGroup className="h-10.5">
                                        <InputGroupInput
                                            aria-label="Other pronouns"
                                            aria-invalid={fieldState.invalid}
                                            aria-describedby={
                                                fieldState.error ? errorId : undefined
                                            }
                                            autoFocus={otherChosenThisSession}
                                            value={field.value ?? ''}
                                            onChange={(event) => field.onChange(event.target.value)}
                                            onBlur={field.onBlur}
                                            disabled={isSubmitting}
                                            maxLength={maxLength}
                                        />
                                        <InputGroupAddon align="inline-end">
                                            <KeyboardIcon className="size-4" />
                                        </InputGroupAddon>
                                    </InputGroup>
                                    <FieldError id={errorId}>
                                        {fieldState.error?.message}
                                    </FieldError>
                                </div>
                            ) : (
                                <RadioButtonItem
                                    value={OTHER_PRONOUN_CHOICE}
                                    title="Other"
                                    size="sm"
                                />
                            )}
                        </RadioGroup>
                    </div>
                );
            }}
        />
    );
}
